// Weight helpers shared by the production label and miscellaneous label pages.
// Keep in sync with the backend twin: app/helpers/weight_format.js

// Balances pad their output to a fixed field width, so trailing zeros are
// display padding rather than a precision claim ("12.36500" is a 3-decimal
// read). Anything beyond this many decimals is a float artefact, not data.
const MAX_WEIGHT_PRECISION = 6;

// Digits after the decimal point that actually carry information, read off the
// balance string as sent — never off the parsed float, which has already lost
// the distinction ("12.36500" -> 3, "0.25" -> 2, "12.00" -> 0).
export const significant_decimals = (value) => {
  if (value === null || value === undefined) return 0;
  const str = String(value).trim();
  // Scientific notation is not something a balance emits; if it ever shows up,
  // fall back to 0 rather than producing a nonsense precision.
  if (!str || str.indexOf('e') !== -1 || str.indexOf('E') !== -1) return 0;
  const dot = str.indexOf('.');
  if (dot === -1) return 0;
  return str.slice(dot + 1).replace(/0+$/, '').length;
};

// Decimal places exactly as the balance displayed them, trailing zeros INCLUDED
// ("7.00000" -> 5, "3.00" -> 2, "12" -> 0). The balance pads to a fixed field
// width and that width is its precision claim, so gross prints at the same
// width as the widest of tare/net and the label reconciles column-for-column:
// 7.00000 + 3.00 reads 10.00000, not 10.
//
// Deliberately NOT significant_decimals, which strips the padding — that one
// stays as it is for log-book totals, where a column of readings should not
// inherit the display padding of a single row.
export const displayed_decimals = (value) => {
  if (value === null || value === undefined) return 0;
  const str = String(value).trim();
  if (!str || str.indexOf('e') !== -1 || str.indexOf('E') !== -1) return 0;
  const dot = str.indexOf('.');
  if (dot === -1) return 0;
  return str.length - dot - 1;
};

// Gross = tare + net, carried at the decimal width of whichever weight the
// balance displayed most widely, so the stored value needs no rounding to reach
// the label and all three weights printed on it share one precision.
// Returns '' when neither weight has been entered, letting each caller supply
// its own empty-state placeholder.
//
// The sum is rounded on scaled integers instead of Number.toFixed() because
// toFixed() rounds the binary float, not the decimal: 11.735.toFixed(2) yields
// "11.73" since the stored double is 11.73499…. Scaling first also collapses
// artefacts like 0.20 + 5.69 = 5.890000000000001.
export const compute_gross_weight = (tare_weight, net_weight) => {
  const tare_str =
    tare_weight === null || tare_weight === undefined
      ? ''
      : String(tare_weight).trim();
  const net_str =
    net_weight === null || net_weight === undefined
      ? ''
      : String(net_weight).trim();
  if (tare_str === '' && net_str === '') return '';

  const tare = parseFloat(tare_str) || 0;
  const net = parseFloat(net_str) || 0;
  const precision = Math.min(
    MAX_WEIGHT_PRECISION,
    Math.max(displayed_decimals(tare_str), displayed_decimals(net_str))
  );

  const scale = Math.pow(10, precision);
  return (Math.round((tare + net) * scale) / scale).toFixed(precision);
};

// Renders a stored weight. Values written by compute_gross_weight are already
// canonical, so this is a no-op on them — it exists only to collapse the float
// artefacts sitting in legacy rows (e.g. 5.890000000000001 -> 5.89). It never
// rounds a real reading away.
export const format_weight = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value).trim();
  if (str === '') return '';
  const num = parseFloat(str);
  if (isNaN(num)) return str;
  const dot = str.indexOf('.');
  const decimals = dot === -1 ? 0 : str.length - dot - 1;
  if (decimals <= MAX_WEIGHT_PRECISION) return str;
  return String(parseFloat(num.toFixed(MAX_WEIGHT_PRECISION)));
};
